import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Connection,
  ConnectionSettings,
  ComponentNode,
  Edge,
  KeyboardShortcuts,
  Node,
  StaticEdge,
  VflowComponent,
  Vflow,
  createEdge,
  createNode
} from 'ngx-vflow';
import { ToastService } from '../../../../../../shared/services/toast.service';
import { WorkflowGraphStore } from '../../store/workflow-graph.store';
import { WorkflowEditorService } from '../../store/workflow-editor.service';
import { WorkflowGraphEdge, WorkflowGraphNode, buildEdgeId } from '../../models/flow-graph.model';
import {
  ORPHAN_HANDLE_ID,
  TARGET_HANDLE_ID,
  getOutputBranchHandles
} from '../../models/node-catalog';
import { buildNodeSummary } from '../../models/node-config.util';
import { WorkflowNodeCardComponent, WorkflowNodeCardData } from './nodes/node-card/node-card.component';
import { WORKFLOW_PALETTE_DRAG_MIME, WorkflowPaletteAddPayload } from '../node-palette/node-palette.component';

const SNAP_GRID: [number, number] = [16, 16];

/**
 * Canvas del nuevo builder de workflow sobre `ngx-vflow` (Fase 1). Reemplaza el canvas a
 * mano del builder viejo (CdkDrag + edges SVG dibujados a mano + modal de branchKey).
 *
 * Se calca de `chatflows/.../flow-canvas` y se adapta a workflow:
 * - PUERTO = `branchKey`: cada puerto de salida de una tarjeta es un `branchKey`
 *   (`getOutputBranchHandles`); arrastrar desde él crea la arista con ESE branchKey vía
 *   `store.connect(sourceNodeKey, branchKey, targetNodeKey)` — sin `<select>` de rama.
 * - Depende SOLO de signals del store (fuente única de reactividad, gotcha §6) y del
 *   editor para persistir (queuePositionPatch al mover, queueAutosave al mutar estructura).
 * - Es de solo lectura cuando el workflow está ACTIVE/ARCHIVED (`editor.readOnly`).
 */
@Component({
  selector: 'app-workflow-flow-canvas',
  standalone: true,
  imports: [CommonModule, Vflow],
  templateUrl: './flow-canvas.component.html',
  // El resaltado por defecto de ngx-vflow apenas se distingue del trazo normal; se
  // refuerza con un selector más específico que el de la librería (misma técnica que
  // chatflow). Los estilos de vflow son de encapsulación emulada.
  styles: [
    `
      :host ::ng-deep path.edge {
        cursor: pointer;
      }
      :host ::ng-deep path.edge.edge_selected,
      :host ::ng-deep path.edge.edge_preselected {
        stroke: #465fff;
        stroke-width: 3.5;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowFlowCanvasComponent {
  private readonly store = inject(WorkflowGraphStore);
  private readonly editor = inject(WorkflowEditorService);
  private readonly toast = inject(ToastService);
  private readonly host = inject(ElementRef<HTMLElement>);

  /** Fuerza el modo lectura además del derivado del editor (ACTIVE/ARCHIVED). */
  readonly readOnly = input(false);

  /** No se puede editar si el input lo pide o si el workflow está ACTIVE/ARCHIVED. */
  protected readonly isReadOnly = computed(() => this.readOnly() || this.editor.readOnly());

  private readonly vflow = viewChild(VflowComponent);

  protected readonly vflowNodes = signal<Node<WorkflowNodeCardData>[]>([]);
  protected readonly vflowEdges = signal<Edge[]>([]);
  protected readonly snapGrid = SNAP_GRID;

  protected readonly background = { type: 'dots', gap: 20, size: 1.4, color: '#cbd5e1' } as const;

  /**
   * INC_CRM_020 (6) inc17 — se recuperan los atajos de selección de vflow: Shift+arrastrar
   * dibuja un recuadro y Ctrl/Cmd+clic suma nodos a la selección.
   *
   * Antes estaban anulados «porque el store y el inspector solo modelan UNA selección», y esa
   * decisión hacía IMPOSIBLE lo que pide el incidente («seleccionar y duplicar un grupo de
   * nodos»): sin selección múltiple no hay grupo que duplicar, cortar ni borrar. La selección
   * única del store sigue siendo válida y no entra en conflicto: es la del INSPECTOR (qué nodo
   * se está configurando) y `onNodeSelectChange` la fija al último marcado, mientras que los
   * atajos y la barra de acciones trabajan sobre el conjunto que reporta el lienzo.
   *
   * Ctrl+D/C/X/V/Z siguen siendo atajos propios: `multiSelection` solo actúa sobre el clic.
   */
  protected readonly keyboardShortcuts: KeyboardShortcuts = {};

  /** Caché por nodeKey: conserva la identidad del nodo de vflow entre reconstrucciones. */
  private readonly nodeCache = new Map<string, ComponentNode<WorkflowNodeCardData>>();

  /**
   * Caché por edgeId: ngx-vflow guarda `selected` en el propio objeto Edge, así que recrear
   * el array en cada pasada del effect lo devolvería a false y deseleccionaría la arista en
   * el mismo tick del clic.
   */
  private readonly edgeCache = new Map<string, Edge>();

  /**
   * Validador de conexión de ngx-vflow (corre DURANTE el arrastre: pinta el destino como
   * válido/inválido antes de soltar). Reusa las reglas duras del store.
   */
  protected readonly connectionSettings: ConnectionSettings = {
    mode: 'strict',
    curve: 'smooth-step',
    allowSelfConnections: false,
    marker: { type: 'arrow-closed' },
    validator: (connection) => {
      if (this.isReadOnly() || connection.source === connection.target) {
        return false;
      }
      const source = this.store.findNode(connection.source);
      const target = this.store.findNode(connection.target);
      if (!source || !target) {
        return false;
      }
      return this.store.isConnectionAllowed(source, target);
    }
  };

  constructor() {
    // Sincroniza el modelo de vflow con el store. Va en un effect (no en un computed)
    // porque escribe signals de vflow (point/data) en sitio, para no perder identidad ni
    // romper un arrastre en curso. NO depende de selection() (ver chatflow): la selección
    // se aplica por su propio canal (nodesChanges.select), no reejecutando este effect.
    effect(() => {
      const nodes = this.store.nodes();
      const edges = this.store.edges();
      const nodeErrors = this.store.nodeErrors();

      const next: ComponentNode<WorkflowNodeCardData>[] = [];
      for (const node of nodes) {
        const data = this.toCardData(node, edges, nodeErrors.has(node.nodeKey));
        const cached = this.nodeCache.get(node.nodeKey);
        if (cached) {
          cached.data?.set(data);
          const point = cached.point();
          if (!this.isDragging && (point.x !== node.positionX || point.y !== node.positionY)) {
            cached.point.set({ x: node.positionX, y: node.positionY });
          }
          next.push(cached);
          continue;
        }
        const created = createNode<WorkflowNodeCardData>({
          id: node.nodeKey,
          type: WorkflowNodeCardComponent,
          point: { x: node.positionX, y: node.positionY },
          data
        }) as ComponentNode<WorkflowNodeCardData>;
        this.nodeCache.set(node.nodeKey, created);
        next.push(created);
      }

      const liveKeys = new Set(nodes.map((node) => node.nodeKey));
      for (const key of [...this.nodeCache.keys()]) {
        if (!liveKeys.has(key)) {
          this.nodeCache.delete(key);
        }
      }

      this.vflowNodes.set(next);
      this.vflowEdges.set(this.syncEdges(nodes, edges));
    });

    // Encuadre inicial: los nodos guardados pueden estar en cualquier coordenada, y el
    // viewport arranca en 0,0 → el lienzo parecería vacío. Se espera a `initialized()`.
    effect(() => {
      const flow = this.vflow();
      const nodeCount = this.store.nodes().length;
      if (!flow || !flow.initialized() || !nodeCount || this.hasFitted()) {
        return;
      }
      this.hasFitted.set(true);
      queueMicrotask(() => this.fitViewInitial());
    });
  }

  private readonly hasFitted = signal(false);

  /**
   * Etiquetas en español de los eventos del CRM, indexadas por clave, para que el resumen de la
   * tarjeta diga "Contacto creado" y no "CONTACT_CREATED". Vienen del propio catálogo del backend,
   * así que no hay un diccionario duplicado que mantener.
   */
  private readonly eventLabels = computed(
    () => new Map(this.editor.crmEvents().map((evento) => [evento.key, evento.label]))
  );

  /** Vuelve a permitir el encuadre inicial (al abrir otro workflow en el shell). */
  resetInitialFit(): void {
    this.hasFitted.set(false);
  }

  private fitViewInitial(): void {
    const flow = this.vflow();
    if (!flow) {
      return;
    }
    flow.fitView({ padding: 0.2 });
    if (flow.viewport().zoom > 1) {
      flow.zoomTo(1);
    }
  }

  /** Conjunto de branchKeys que SÍ son puerto real del nodo (para separar los huérfanos). */
  private handleKeysOf(node: WorkflowGraphNode): Set<string> {
    return new Set(getOutputBranchHandles(node).map((h) => h.branchKey));
  }

  private toCardData(
    node: WorkflowGraphNode,
    edges: readonly WorkflowGraphEdge[],
    incomplete: boolean
  ): WorkflowNodeCardData {
    const handles = getOutputBranchHandles(node);
    const handleKeys = new Set(handles.map((h) => h.branchKey));
    const orphanBranchKeys: string[] = [];
    for (const edge of edges) {
      if (edge.sourceNodeKey === node.nodeKey && !handleKeys.has(edge.branchKey) && !orphanBranchKeys.includes(edge.branchKey)) {
        orphanBranchKeys.push(edge.branchKey);
      }
    }
    return {
      nodeKey: node.nodeKey,
      nodeType: node.nodeType,
      title: node.title,
      summary: buildNodeSummary(node, this.eventLabels()),
      handles,
      orphanBranchKeys,
      hasIncoming: edges.some((edge) => edge.targetNodeKey === node.nodeKey),
      incomplete,
      isActive: node.isActive
    };
  }

  /**
   * Reconstruye las aristas conservando la identidad de las existentes. El id local
   * (origen|branchKey|destino) es estable entre guardados, a diferencia del id de BD.
   * El puerto de origen es el `branchKey`; si ese branchKey ya no es puerto real (huérfano),
   * la arista se ancla al puerto huérfano para poder verse y borrarse.
   */
  private syncEdges(nodes: readonly WorkflowGraphNode[], edges: readonly WorkflowGraphEdge[]): Edge[] {
    const handleKeysByNode = new Map<string, Set<string>>();
    for (const node of nodes) {
      handleKeysByNode.set(node.nodeKey, this.handleKeysOf(node));
    }

    const next: Edge[] = [];
    for (const edge of edges) {
      const id = buildEdgeId(edge);
      const isOrphan = !handleKeysByNode.get(edge.sourceNodeKey)?.has(edge.branchKey);
      const labels = this.toEdgeLabels(edge, isOrphan);
      const cached = this.edgeCache.get(id);
      if (cached) {
        cached.edgeLabels?.set(labels);
        next.push(cached);
        continue;
      }
      const created = createEdge({
        id,
        source: edge.sourceNodeKey,
        target: edge.targetNodeKey,
        sourceHandle: isOrphan ? ORPHAN_HANDLE_ID : edge.branchKey,
        targetHandle: TARGET_HANDLE_ID,
        curve: 'smooth-step',
        type: 'default',
        markers: { end: { type: 'arrow-closed' } },
        edgeLabels: labels
      });
      this.edgeCache.set(id, created);
      next.push(created);
    }

    const liveIds = new Set(next.map((edge) => edge.id));
    for (const id of [...this.edgeCache.keys()]) {
      if (!liveIds.has(id)) {
        this.edgeCache.delete(id);
      }
    }
    return next;
  }

  private toEdgeLabels(edge: WorkflowGraphEdge, isOrphan: boolean): NonNullable<StaticEdge['edgeLabels']> {
    const label = edge.label.trim() || (edge.branchKey !== 'DEFAULT' ? edge.branchKey : '');
    if (!label) {
      return {};
    }
    return {
      center: {
        type: 'default',
        text: label,
        style: { fontSize: '11px', fill: isOrphan ? '#d97706' : '#6b7280' }
      }
    };
  }

  // ── Conexión ────────────────────────────────────────────────────────────────

  /** El puerto de origen determina el branchKey: aquí solo se traduce el arrastre. */
  protected onConnect(connection: Connection): void {
    if (this.isReadOnly() || !connection.sourceHandle) {
      return;
    }
    // El puerto huérfano no crea aristas nuevas (solo existe para ver/borrar las heredadas).
    if (connection.sourceHandle === ORPHAN_HANDLE_ID) {
      return;
    }
    if (this.store.connect(connection.source, connection.sourceHandle, connection.target)) {
      this.editor.queueAutosave();
    }
  }

  // ── Paleta → canvas (soltar un paso) ──────────────────────────────────────────

  protected readonly isDraggingOver = signal(false);

  protected onDragOver(event: DragEvent): void {
    if (this.isReadOnly()) {
      return;
    }
    // Sin preventDefault el navegador no dispara `drop`.
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    this.isDraggingOver.set(true);
  }

  protected onDragLeave(event: DragEvent): void {
    if (!this.host.nativeElement.contains(event.relatedTarget as globalThis.Node | null)) {
      this.isDraggingOver.set(false);
    }
  }

  /**
   * El paso nace BAJO EL CURSOR: `documentPointToFlowPoint` aplica la inversa de la matriz
   * de pantalla del SVG, así que respeta zoom y paneo. El branchKey/puertos se derivan luego
   * del nodo creado; aquí solo se crea el nodo y se programa el autosave (el mismo canal que
   * el alta por clic desde la paleta).
   */
  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingOver.set(false);
    if (this.isReadOnly()) {
      return;
    }
    const raw = event.dataTransfer?.getData(WORKFLOW_PALETTE_DRAG_MIME) || event.dataTransfer?.getData('text/plain');
    const payload = this.parsePayload(raw);
    if (!payload) {
      return;
    }
    // El disparador es único: la paleta ya lo deshabilita, pero se blinda aquí también.
    if (payload.nodeType === 'TRIGGER' && this.store.hasTriggerNode()) {
      return;
    }
    const flow = this.vflow();
    if (!flow) {
      return;
    }
    const point = flow.documentPointToFlowPoint({ x: event.clientX, y: event.clientY });
    this.store.addNode(payload.nodeType, payload.nodeSubtype, point.x, point.y, {
      title: payload.title,
      description: payload.description,
      config: payload.config
    });
    this.editor.queueAutosave();
  }

  private parsePayload(raw: string | undefined): WorkflowPaletteAddPayload | null {
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as WorkflowPaletteAddPayload;
      return parsed && typeof parsed.nodeType === 'string' ? parsed : null;
    } catch {
      return null;
    }
  }

  // ── Arrastre / posición ───────────────────────────────────────────────────────

  private isDragging = false;

  protected onNodeDragStart(): void {
    this.isDragging = true;
  }

  protected onNodeDragEnd(event: { node: Node }): void {
    this.isDragging = false;
    if (this.isReadOnly()) {
      return;
    }
    const point = event.node.point();
    const current = this.store.findNode(event.node.id);
    // d3-drag emite `end` también en un clic sin desplazamiento: solo cuenta si se movió.
    if (
      current &&
      Math.round(current.positionX) === Math.round(point.x) &&
      Math.round(current.positionY) === Math.round(point.y)
    ) {
      return;
    }
    this.store.moveNode(event.node.id, point.x, point.y);
    this.editor.queuePositionPatch({
      nodeKey: event.node.id,
      positionX: Math.round(point.x),
      positionY: Math.round(point.y)
    });
  }

  // ── Selección ─────────────────────────────────────────────────────────────────

  protected onNodeSelectChange(changes: Array<{ id: string; selected: boolean }>): void {
    const last = changes.filter((change) => change.selected).pop();
    if (last) {
      this.store.setSelection({ kind: 'node', nodeKey: last.id });
    } else {
      this.clearSelectionIfNothingSelected();
    }
  }

  protected onEdgeSelectChange(changes: Array<{ id: string; selected: boolean }>): void {
    const last = changes.filter((change) => change.selected).pop();
    if (last) {
      this.store.setSelection({ kind: 'edge', edgeId: last.id });
    } else {
      this.clearSelectionIfNothingSelected();
    }
  }

  private clearSelectionIfNothingSelected(): void {
    if (!this.selectedNodeKeys().length && !this.selectedEdgeId()) {
      this.store.setSelection(null);
    }
  }

  // ── Atajos de teclado (INC_CRM_020 (4) inc14) ─────────────────────────────────
  //
  // El manejador colgaba de `(keydown)` en el div del lienzo, que solo recibe teclas cuando
  // ESE div tiene el foco. En cuanto el usuario tocaba el inspector, la paleta o la barra
  // superior —o simplemente no había pinchado el fondo del lienzo— Ctrl+Z no llegaba a
  // ninguna parte: de ahí el "solo funciona sobre el último nodo, no sobre el flujo". Ahora
  // se escucha en `document`, así que los atajos valen en toda la pantalla del constructor.
  // El componente solo existe dentro del builder, de modo que el listener muere con él.

  @HostListener('document:keydown', ['$event'])
  protected onKeyDown(event: KeyboardEvent): void {
    if (this.isReadOnly()) {
      return;
    }
    // Mientras se escribe, las teclas son del campo: Ctrl+Z ahí deshace el TEXTO (undo nativo
    // del navegador) y Ctrl+X corta la selección de texto. Interceptarlas rompería la edición.
    const target = event.target as HTMLElement | null;
    if (target && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable)) {
      return;
    }
    const ctrl = event.ctrlKey || event.metaKey;

    if (ctrl && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      if (event.shiftKey) {
        this.store.redo();
      } else {
        this.store.undo();
      }
      this.editor.queueAutosave();
      return;
    }
    if (ctrl && event.key.toLowerCase() === 'y') {
      event.preventDefault();
      this.store.redo();
      this.editor.queueAutosave();
      return;
    }
    if (ctrl && event.key.toLowerCase() === 'x') {
      event.preventDefault();
      if (this.store.cutNodes(this.activeNodeKeys())) {
        this.editor.queueAutosave();
      }
      return;
    }
    if (ctrl && event.key.toLowerCase() === 'c') {
      event.preventDefault();
      this.store.copyNodes(this.activeNodeKeys());
      return;
    }
    if (ctrl && event.key.toLowerCase() === 'v') {
      event.preventDefault();
      if (this.store.pasteClipboard().length) {
        this.editor.queueAutosave();
      }
      return;
    }
    if (ctrl && event.key.toLowerCase() === 'd') {
      event.preventDefault();
      const keys = this.activeNodeKeys();
      if (keys.length && this.store.duplicateNodes(keys).length) {
        this.editor.queueAutosave();
      }
      return;
    }
    if (event.key === 'Delete' || event.key === 'Supr') {
      const keys = this.activeNodeKeys();
      if (keys.length) {
        event.preventDefault();
        this.store.removeNodes(keys);
        this.editor.queueAutosave();
        return;
      }
      const edgeId = this.selectedEdgeId();
      if (edgeId) {
        event.preventDefault();
        this.store.removeEdge(edgeId);
        this.editor.queueAutosave();
      }
    }
  }

  /**
   * Nodos sobre los que actúa un atajo.
   *
   * Se prefiere la selección del propio lienzo (permite varios nodos a la vez), pero si vflow
   * no reporta ninguno se cae a la selección del store: al configurar un nodo desde el
   * inspector el foco sale del lienzo y vflow deja de marcarlo como `selected`, aunque para el
   * usuario ese sigue siendo "el nodo en el que está trabajando".
   */
  private activeNodeKeys(): string[] {
    const desdeCanvas = this.selectedNodeKeys();
    if (desdeCanvas.length) {
      return desdeCanvas;
    }
    const seleccion = this.store.selection();
    return seleccion?.kind === 'node' ? [seleccion.nodeKey] : [];
  }

  /** Claves de los nodos seleccionados. */
  private selectedNodeKeys(): string[] {
    return this.vflowNodes()
      .filter((node) => node.selected?.())
      .map((node) => node.id);
  }

  /** Id de la conexión seleccionada, si la hay. */
  private selectedEdgeId(): string | null {
    return this.vflowEdges().find((edge) => edge.selected?.())?.id ?? null;
  }

  // ── Acciones sobre la selección (INC_CRM_020 (6) inc17) ─────────────────────────
  //
  // Duplicar ya existía por Ctrl+D y desde el inspector, pero el inspector solo muestra UN nodo
  // y un atajo de teclado no se descubre solo: QA lo reportó como "no existe la opción de
  // duplicar". Esta barra aparece en cuanto hay selección, dice cuántos nodos hay y es la única
  // vía para duplicar un GRUPO entero.

  /** Misma selección que usan los atajos, pero como signal para poder pintarla. */
  protected readonly activeSelection = computed<string[]>(() => {
    const desdeCanvas = this.vflowNodes()
      .filter((node) => node.selected?.())
      .map((node) => node.id);
    if (desdeCanvas.length) {
      return desdeCanvas;
    }
    const seleccion = this.store.selection();
    return seleccion?.kind === 'node' ? [seleccion.nodeKey] : [];
  });

  /** Cuántos de los seleccionados se pueden duplicar de verdad (los disparadores no). */
  protected readonly duplicableCount = computed(() => {
    const keys = new Set(this.activeSelection());
    return this.store.nodes().filter((node) => keys.has(node.nodeKey) && node.nodeType !== 'TRIGGER').length;
  });

  protected duplicateSelection(): void {
    const keys = this.activeSelection();
    if (!keys.length) {
      return;
    }
    const created = this.store.duplicateNodes(keys);
    if (!created.length) {
      this.toast.warning({
        title: 'No se duplicó',
        message: 'El disparador no se puede duplicar: un workflow solo admite uno.'
      });
      return;
    }
    this.editor.queueAutosave();
    this.toast.success({
      title: created.length === 1 ? 'Nodo duplicado' : 'Nodos duplicados',
      message: created.length === 1
        ? 'La copia conserva la configuración del original.'
        : `${created.length} nodos duplicados con sus conexiones internas.`
    });
  }

  // ── Controles de zoom ───────────────────────────────────────────────────────────

  protected readonly zoomLabel = computed(() => {
    const zoom = this.vflow()?.viewport().zoom ?? 1;
    return `${Math.round(zoom * 100)}%`;
  });

  protected zoomIn(): void {
    const flow = this.vflow();
    flow?.zoomTo(Math.min(flow.viewport().zoom * 1.2, 3));
  }

  protected zoomOut(): void {
    const flow = this.vflow();
    flow?.zoomTo(Math.max(flow.viewport().zoom / 1.2, 0.2));
  }

  protected fitView(): void {
    this.vflow()?.fitView({ padding: 0.2, duration: 200 });
  }

  /** Centra y selecciona un nodo (p. ej. al pulsar un aviso de validación). */
  focusNode(nodeKey: string): void {
    this.vflow()?.fitView({ padding: 0.6, duration: 300, nodes: [nodeKey] });
    for (const node of this.vflowNodes()) {
      node.selected?.set(node.id === nodeKey);
    }
    for (const edge of this.vflowEdges()) {
      edge.selected?.set(false);
    }
    this.store.setSelection({ kind: 'node', nodeKey });
  }
}
